import memoizee from 'memoizee';
import tinycolor from 'tinycolor2';
import { type ThemeType } from '~/_shared/themes/themeType';
import { clamp } from '~/_shared/utils/number/number.helpers';
import { notNullsy } from '~/_shared/utils/typeGuards';
import {
  createColor, guaranteeHash, rgbColorToString,
} from '../../components/colorPicker/colorPicker.helpers';
import {
  type ColorResult, type RGBColor,
} from '../../components/colorPicker/colorPicker.types';
import { memoizeWeak } from '../memoize/memoize';
import { type PerThemeColor } from './colors.types';

const MIN_READABLE_CONTRAST = 4.5;
export const BARELY_READABLE_CONTRAST = 1.2;
const LIGHT_DARK_THRESHOLD = 130;

export const getNextColorFromPresets = (usedColors: ReadonlyArray<string>, presetColors: readonly [string, ...string[]]): string => {
  const normalizedPresetColors = presetColors.map(color => guaranteeHash(color).toUpperCase());
  const normalizedUsedColors = usedColors.map(color => guaranteeHash(color).toUpperCase());

  let lastUsedPresetColor = normalizedPresetColors[normalizedPresetColors.length - 1];

  for (const usedRouteColor of normalizedUsedColors) {
    if (normalizedPresetColors.includes(usedRouteColor)) {
      lastUsedPresetColor = usedRouteColor;
    }
  }

  let indexOfNewColor = normalizedPresetColors.indexOf(lastUsedPresetColor ?? '') + 1;
  if (indexOfNewColor > normalizedPresetColors.length - 1) {
    indexOfNewColor = 0;
  }

  // TODO: Change to @ts-expect-error when we transition to the stricter TS config.
  // eslint-disable-next-line
  // @ts-ignore: The indexing is made safe by the condition above.
  return normalizedPresetColors[indexOfNewColor];
};

export const changeColorAlpha = memoizeWeak((color: RGBColor, newAlpha: number | undefined): ColorResult => {
  const newColor = { ...color };
  newColor.a = newAlpha;

  return createColor(newColor);
});

export const convertColorToWebGLColor = memoizee((color: string, opacity?: number): WebglColor => {
  const parsedColor = createColor(color).rgb;
  const base = [parsedColor.r, parsedColor.g, parsedColor.b] as const;

  return [...base, opacity ?? 1];
});

export const webGLColorToString = (color: WebglColor): string =>
  `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${color[3]})`;

export const convertRGBColorToWebGLColor = memoizeWeak((color: RGBColor): WebglColor =>
  Object.values({ ...color, a: color.a || 1 }) as unknown as WebglColor);

const getColorBrightness = (color: RGBColor) =>
  Math.sqrt(
    color.r * color.r * .241 +
    color.g * color.g * .691 +
    color.b * color.b * .068,
  );

export const getSuitableBorderColor = (baseColor: ColorResult, backgroundColor?: ColorResult): ColorResult => {
  const backgroundBrightness = getColorBrightness((backgroundColor || baseColor).rgb);
  const baseBrightness = getColorBrightness(baseColor.rgb);

  let { l: baseLightness, s: baseSaturation } = baseColor.hsl;
  const baseHue = baseColor.hsl.h;

  // Choose a delta based on the hue
  let delta = 0.15; // default
  if (baseHue >= 30 && baseHue <= 70) {
    // For yellows
    delta = 0.25;
  }
  else if (baseHue > 70 && baseHue < 180) {
    // For greens
    delta = 0.20;
  }
  else if (baseHue >= 180 && baseHue <= 240) {
    // For blues
    delta = 0.20;
  }
  else if (baseHue >= 300 && baseHue <= 330) {
    // For pinks/magentas
    delta = 0.20;
  }
  else if (baseHue < 30 || baseHue > 330) {
    // For reds
    delta = 0.25;
  }

  if (Math.abs(backgroundBrightness - baseBrightness) < 35) {
    if (backgroundBrightness > LIGHT_DARK_THRESHOLD) {
      baseLightness -= delta;
    }
    else {
      if (baseLightness < 0.05) {
        baseSaturation = 0.02;
        baseLightness = 0.6;
      }
      else {
        baseLightness += delta;
      }
    }
  }

  return createColor({
    ...baseColor.hsl,
    l: baseLightness,
    s: baseSaturation,
  });
};

export const getSuitableBorderColorString = (baseColor: string, backgroundColor: string): string => {
  return rgbColorToString(getSuitableBorderColor(createColor(baseColor), createColor(backgroundColor)).rgb);
};

export const makeColorLighter = (baseColor: string, lightnessDifference = 30): ColorResult => {
  const colorResultColor = createColor(baseColor);
  const newHslColor = {
    ...colorResultColor.hsl,
    l: Math.min(colorResultColor.hsl.l + lightnessDifference / 100, 1),
  };

  return createColor(newHslColor);
};

export const makeColorDarker = (baseColor: string, lightnessDifference = 20): ColorResult => {
  const colorResultColor = createColor(baseColor);

  const newHslColor = {
    ...colorResultColor.hsl,
    l: Math.max(colorResultColor.hsl.l - lightnessDifference / 100, 0),
  };

  return createColor(newHslColor);
};

/**
 * Checks if the contrast between a base color and an optional background color is sufficient, using the WCAG contrast
 * ratio.
 *
 * @param baseColor - The base (foreground) color as a hex code.
 * @param backgroundColor - The background color as a hex code.
 * @param minContrastRatio - The minimum acceptable contrast ratio (default is 4.5).
 * @returns True if the contrast ratio between baseColor and backgroundColor is at least minContrastRatio,
 *          otherwise false.
 */
export const isContrastSufficient = (
  baseColor: string,
  backgroundColor?: string,
  minContrastRatio: number = MIN_READABLE_CONTRAST
): boolean =>
  backgroundColor
    ? tinycolor.readability(baseColor, backgroundColor) >= minContrastRatio
    : false;

/**
 * Gets a contrast color based on the base color and optional background color.
 *
 * @param baseColor - The base color to get the contrast for.
 * @param backgroundColor - The background color to compare against.
 * @param options - Optional settings for the contrast calculation.
 * @param options.tolerance - The tolerance level for brightness difference.
 * Brightness difference below this level will trigger a different contrast color.
 * Default is 85. Max value is 255 and min value is 0.
 * @returns The contrast color.
 */
export const getContrastColor = (
  baseColor: ColorResult,
  backgroundColor?: ColorResult,
  options?: { tolerance?: number }
) => getContrastColorMemoized(baseColor, backgroundColor, options?.tolerance);

const getContrastColorMemoized = memoizeWeak((
  baseColor: ColorResult,
  backgroundColor: ColorResult | undefined,
  tolerance?: number,
): ColorResult => {
  const backgroundBrightness = backgroundColor && getColorBrightness(backgroundColor.rgb);
  const baseBrightness = getColorBrightness(baseColor.rgb);

  let baseLightness = baseColor.hsl.l;

  if (!isContrastSufficient(baseColor.hex, backgroundColor?.hex, tolerance)) {
    if ((backgroundBrightness ?? baseBrightness) > 130) {
      baseLightness = 0.15;
    }
    else {
      baseLightness = 0.85;
    }
  }

  return createColor({
    ...baseColor.hsl,
    l: baseLightness,
  });
});

export const getDarkOrLightDependingOnBaseColor = (baseColor: ColorResult, darkColor: ColorResult, lightColor: ColorResult): ColorResult => {
  const baseBrightness = getColorBrightness(baseColor.rgb);

  return baseBrightness > LIGHT_DARK_THRESHOLD ? darkColor : lightColor;
};

export const getFontAndBorderColorOnBackground = (
  fontColor: ColorResult, baseColor: ColorResult, backgroundColor: ColorResult,
): [borderColor: ColorResult, fontColor: ColorResult] => {
  const contrastTextColor = getContrastColor(fontColor, baseColor);
  const borderColor = getSuitableBorderColor(baseColor, backgroundColor);

  return [borderColor, contrastTextColor];
};

export const mixColorMemoized = memoizee((
  c1: string, c2: string, amount?: number,
) => tinycolor.mix(c1, c2, notNullsy(amount) ? amount * 100 : undefined));

export const getPerThemeColor = (perThemeColor: PerThemeColor, currentTheme: ThemeType): string | null => (
  perThemeColor[currentTheme] ?? null
);

/**
 * Appends an alpha value to a hex color using TinyColor.
 *
 * @param hex - A 6-digit hex color (with or without a leading '#').
 * @param opacity - A number between 0 and 1 representing opacity.
 * @returns An 8-digit hex color string (with a leading '#').
 */
export function hexWithOpacity(hex: string, opacity: number): string {
  const clampedOpacity = clamp(opacity, { min: 0, max: 1 });

  return tinycolor(hex)
    .setAlpha(clampedOpacity)
    .toHex8String()
    .toUpperCase();
}
